import { Stack, Typography } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Skeleton } from '@/shared/components/ui/skeleton/skeleton.styled';
import { Tag } from '@/shared/components/ui/tag/tag';
import { Tooltip } from '@/shared/components/ui/tooltip/tooltip';
import { TGroupNameProps } from '@/shared/features/groups/components/group-name/group-name.types';
import { useGroupsConfig } from '@/shared/features/groups/context/groups-config-context';
import { useReadResourceGroupNameByIdQuery } from '@/shared/redux/api/assets/groups/groups-api';
import { truncateText } from '@/shared/utils/truncate-text';

export const GroupName: React.FC<TGroupNameProps> = ({
  groupId,
  variant = 'tag',
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { groupRoutePath, useCanReadGroup } = useGroupsConfig();
  const hasPermission = useCanReadGroup(groupId);
  const isText = variant === 'text';
  const { data, isLoading } = useReadResourceGroupNameByIdQuery(
    { groupId: groupId! },
    { skip: !groupId || !hasPermission },
  );
  const group = data?.readResourceGroup;
  const getFullPaths = (): string[] => {
    if (group?.fullPaths?.length) {
      return group.fullPaths;
    }
    return group?.name ? [group.name] : [];
  };
  const fullPaths = getFullPaths();

  const getLabel = () => {
    if (fullPaths.length <= 2) {
      return fullPaths.join(' / ');
    }
    return `${fullPaths.at(0)} /.../ ${fullPaths.at(-1)}`;
  };

  const { shouldTruncate, truncatedText } = truncateText(getLabel(), 24, true);

  const getTooltipTitle = () => {
    if (fullPaths.length <= 2 && !shouldTruncate) {
      return '';
    }
    return fullPaths.join(' / ');
  };

  if (!groupId) {
    return '-';
  }

  if (!hasPermission) {
    if (isText) {
      return (
        <Typography variant="inherit" component="span">
          {t('groups:restricted_group')}
        </Typography>
      );
    }
    return (
      <Stack direction="row" alignItems="center" gap="0.25rem">
        <Tag
          label={
            <Typography variant="p8">{t('groups:restricted_group')}</Typography>
          }
        />
      </Stack>
    );
  }

  if (isLoading) {
    return <Skeleton />;
  }

  if (fullPaths.length === 0) {
    return '-';
  }

  if (isText) {
    return (
      <Tooltip title={getTooltipTitle()} noMaxWidth>
        <Typography variant="inherit" component="span">
          {truncatedText}
        </Typography>
      </Tooltip>
    );
  }

  return (
    <Stack direction="row" alignItems="center" gap="0.25rem">
      <Tooltip title={getTooltipTitle()} noMaxWidth>
        <Tag
          label={<Typography variant="p8">{truncatedText}</Typography>}
          onClick={() => navigate(`${groupRoutePath}/${groupId}`)}
        />
      </Tooltip>
    </Stack>
  );
};
